import React from "react";
import { useTranslation } from "react-i18next";

/**
 * CarryForwardBadge
 *
 * Props:
 * - allowed: boolean (required)  — whether carry forward is allowed
 * - className: string (optional)  — outer container class
 * - docsUrl: string (optional)     — link to documentation
 *
 * Renders a small info icon + short label, with an accessible tooltip.
 */

const InfoSvg = ({ titleId, title }) => (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" role="img" aria-labelledby={titleId}>
        <title id={titleId}>{title}</title>
        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z" fill="currentColor" opacity="0.08" />
        <path d="M12 11c.552 0 1 .448 1 1v4c0 .552-.448 1-1 1s-1-.448-1-1v-4c0-.552.448-1 1-1zm0-6c.828 0 1.5.672 1.5 1.5S12.828 8 12 8s-1.5-.672-1.5-1.5S11.172 5 12 5z" fill="currentColor" />
    </svg>
);

const CarryIcon = ({ allowed }) => {
    if (allowed) {
        return (
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" role="img">
                <path d="M9 16.2l-3.5-3.5L4 14.2 9 19l11-11-1.5-1.5z" fill="currentColor" />
            </svg>
        );
    }
    return (
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" role="img">
            <path d="M6 12h12v1H6z" fill="currentColor" />
        </svg>
    );
};

export default function CarryForwardBadge({ allowed = false, className = "", docsUrl = null }) {
    const { t } = useTranslation();
    const id = `cfb-${Math.random().toString(36).slice(2, 9)}`;
    const label = allowed
        ? t("batches.carryForwardBadge.allowed")
        : t("batches.carryForwardBadge.notAllowed");
    const tooltip = allowed
        ? t("batches.carryForwardBadge.tooltipAllowed")
        : t("batches.carryForwardBadge.tooltipNotAllowed");

    return (
        <div className={`inline-flex items-center gap-2 ${className}`}>
            <span
                className="flex items-center gap-1 text-sm text-gray-700"
                tabIndex={0}
                role="button"
                aria-describedby={id}
                title={label}
            >
                <span className="flex items-center justify-center w-5 h-5 rounded bg-gray-100 text-gray-700">
                    <CarryIcon allowed={allowed} />
                </span>
                <span className="sr-only">{label}</span>
            </span>

            <div
                id={id}
                role="tooltip"
                className="inline-block ml-1 text-xs text-gray-600 bg-white border border-gray-200 shadow-sm p-2 rounded z-50"
                style={{ display: "none" }}
            >
                <div className="flex items-start gap-2">
                    <div className="w-4 h-4"><InfoSvg titleId={`${id}-title`} title={t("common.info")} /></div>
                    <div className="text-xs">{tooltip}</div>
                </div>
                {docsUrl && (
                    <div className="mt-1">
                        <a href={docsUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">
                            {t("common.learnMore")}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
